import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import type { Row } from "@rocicorp/zero";
import {
  appMutators,
  appQueries,
  type AppZeroSchema,
} from "@production-hub/shared";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { toFriendlyMutationError } from "@/lib/error-utils";
import { generateUUID } from "@/lib/utils";
import {
  calculateLineItemTotal,
  calculateGroupTotal,
  calculateFinanceTotals,
} from "@/lib/finance-calculations";
import type { LineItemType } from "@/types/finance-entities";

export { calculateLineItemTotal, calculateGroupTotal, calculateFinanceTotals };

type AppZeroRows = Row<AppZeroSchema>;

export type InvoiceGroupEntity = AppZeroRows["invoiceGroups"];
export type InvoiceLineItemEntity = AppZeroRows["invoiceLineItems"];
export type InvoiceDeductionItemEntity =
  AppZeroRows["invoiceDeductionItems"] & {
    sourceInvoiceID: string | null;
  };

export type InvoiceGroupWithItems = InvoiceGroupEntity & {
  items: InvoiceLineItemEntity[];
};

type ZeroMutationResult =
  | { type: "error"; error: { message: string } }
  | { type: string; error?: never };
type ZeroMutateClient = {
  mutate: (mutation: unknown) => {
    client: Promise<void>;
    server: Promise<ZeroMutationResult>;
  };
};
type MutationOptions<T> = { onSuccess?: (data: T) => void };

type ZeroInvoiceGroupRow = InvoiceGroupEntity & {
  items?: ZeroInvoiceLineItemRow[];
};

type ZeroInvoiceLineItemRow = InvoiceLineItemEntity;

type ZeroInvoiceDeductionItemRow = InvoiceDeductionItemEntity;

function normalizeGroup(row: ZeroInvoiceGroupRow): InvoiceGroupWithItems {
  return {
    ...row,
    items: [...(row.items ?? [])].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    ),
  };
}

function normalizeDeduction(
  row: ZeroInvoiceDeductionItemRow,
): InvoiceDeductionItemEntity {
  return {
    ...row,
    sourceInvoiceID: row.sourceInvoiceID ?? null,
  };
}

async function reportZeroMutationFailure(
  serverResult: Promise<ZeroMutationResult>,
  fallback: string,
) {
  const result = await serverResult;
  if (result.type === "error") {
    throw new Error(result.error?.message || fallback);
  }
}

function useZeroMutation<TInput, TResult>({
  run,
  failure,
}: {
  run: (input: TInput) => Promise<TResult>;
  failure: string;
}) {
  const [isPending, setIsPending] = useState(false);
  const wrappedRun = async (input: TInput) => {
    setIsPending(true);
    try {
      return await run(input);
    } catch (error) {
      throw toFriendlyMutationError(error);
    } finally {
      setIsPending(false);
    }
  };
  return {
    mutate: (input: TInput, options?: MutationOptions<TResult>) => {
      void wrappedRun(input)
        .then((data) => options?.onSuccess?.(data))
        .catch((error) => toast.error(`${failure}: ${error.message}`));
    },
    mutateAsync: wrappedRun,
    isPending,
  };
}

export function useInvoiceGroupsWithItems(invoiceId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.invoiceGroups.byInvoice({
      invoiceID: invoiceId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const groups = useMemo(
    () =>
      ((data ?? []) as ZeroInvoiceGroupRow[])
        .map(normalizeGroup)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [data],
  );
  return {
    data: invoiceId ? groups : [],
    isLoading:
      !!invoiceId && details.type !== "complete" && groups.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useInvoiceDeductionItems(invoiceId: string | undefined) {
  const [data, details] = useZeroQuery(
    appQueries.invoiceDeductionItems.byInvoice({
      invoiceID: invoiceId ?? "00000000-0000-0000-0000-000000000000",
    }),
  );
  const deductions = useMemo(
    () =>
      ((data ?? []) as ZeroInvoiceDeductionItemRow[])
        .map(normalizeDeduction)
        .sort((a, b) => a.displayOrder - b.displayOrder),
    [data],
  );
  return {
    data: invoiceId ? deductions : [],
    isLoading:
      !!invoiceId && details.type !== "complete" && deductions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useCreateInvoiceGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      invoiceId: string;
      name?: string;
      displayOrder?: number;
      isOptional?: boolean;
      applyProjectDiscount?: boolean;
    },
    InvoiceGroupEntity
  >({
    failure: "Failed to create group",
    run: async (vars) => {
      const id = generateUUID();
      const now = Date.now();
      const mutation = zero.mutate(
        appMutators.invoiceGroups.create({
          id,
          invoiceID: vars.invoiceId,
          name: vars.name ?? "New Group",
          displayOrder: vars.displayOrder ?? 0,
          isOptional: vars.isOptional ?? false,
          applyProjectDiscount: vars.applyProjectDiscount ?? true,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group create rejected");
      return {
        id,
        invoiceID: vars.invoiceId,
        name: vars.name ?? "New Group",
        displayOrder: vars.displayOrder ?? 0,
        isOptional: vars.isOptional ?? false,
        applyProjectDiscount: vars.applyProjectDiscount ?? true,
        createdAt: now,
        updatedAt: now,
      };
    },
  });
}

export function useUpdateInvoiceGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        name: string;
        displayOrder: number;
        isOptional: boolean;
        applyProjectDiscount: boolean;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update group",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.invoiceGroups.update({
          id,
          name: updates.name,
          displayOrder: updates.displayOrder,
          isOptional: updates.isOptional,
          applyProjectDiscount: updates.applyProjectDiscount,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group update rejected");
      return { id };
    },
  });
}

export function useDeleteInvoiceGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<string, string>({
    failure: "Failed to delete group",
    run: async (id) => {
      const mutation = zero.mutate(appMutators.invoiceGroups.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(mutation.server, "Group delete rejected");
      return id;
    },
  });
}

export function useDuplicateInvoiceGroup() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { groupId: string; group?: InvoiceGroupWithItems },
    { id: string }
  >({
    failure: "Failed to duplicate group",
    run: async ({ groupId, group }) => {
      const newID = generateUUID();
      const itemIDs =
        group?.items.map((item) => ({
          sourceID: item.id,
          newID: generateUUID(),
        })) ?? [];
      const mutation = zero.mutate(
        appMutators.invoiceGroups.duplicate({ id: groupId, newID, itemIDs }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Group duplicate rejected",
      );
      return { id: newID };
    },
  });
}

export function useCreateInvoiceLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      invoiceGroupId: string;
      invoiceId?: string;
      name?: string;
      amount?: number;
      factor?: number;
      unitPrice?: number;
      discount?: number;
      notes?: string;
      displayOrder?: number;
      optimisticId?: string;
      isTitle?: boolean;
      lineItemType?: LineItemType;
    },
    InvoiceLineItemEntity
  >({
    failure: "Failed to create line item",
    run: async (vars) => {
      const id = vars.optimisticId ?? generateUUID();
      const now = Date.now();
      const mutation = zero.mutate(
        appMutators.invoiceLineItems.create({
          id,
          invoiceGroupID: vars.invoiceGroupId,
          name: vars.name ?? "",
          amount: vars.amount ?? 1,
          factor: vars.factor ?? 1,
          unitPrice: vars.unitPrice ?? 0,
          discount: vars.discount ?? 0,
          notes: vars.notes ?? "",
          displayOrder: vars.displayOrder ?? 0,
          isTitle: vars.isTitle ?? false,
          lineItemType: vars.lineItemType ?? "generic",
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item create rejected",
      );
      return {
        id,
        invoiceGroupID: vars.invoiceGroupId,
        name: vars.name ?? "",
        amount: vars.amount ?? 1,
        factor: vars.factor ?? 1,
        unitPrice: vars.unitPrice ?? 0,
        discount: vars.discount ?? 0,
        notes: vars.notes ?? "",
        externalNote: "",
        displayOrder: vars.displayOrder ?? 0,
        isTitle: vars.isTitle ?? false,
        lineItemType: vars.lineItemType ?? "generic",
        createdAt: now,
        updatedAt: now,
      };
    },
  });
}

export function useUpdateInvoiceLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        invoiceGroupID: string;
        name: string;
        amount: number;
        factor: number;
        unitPrice: number;
        discount: number;
        notes: string;
        externalNote: string;
        displayOrder: number;
        lineItemType: LineItemType;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update line item",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.invoiceLineItems.update({
          id,
          invoiceGroupID: updates.invoiceGroupID,
          name: updates.name,
          amount: updates.amount,
          factor: updates.factor,
          unitPrice: updates.unitPrice,
          discount: updates.discount,
          notes: updates.notes,
          externalNote: updates.externalNote,
          displayOrder: updates.displayOrder,
          lineItemType: updates.lineItemType,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteInvoiceLineItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<{ id: string; invoiceId?: string }, string>({
    failure: "Failed to delete line item",
    run: async ({ id }) => {
      const mutation = zero.mutate(appMutators.invoiceLineItems.delete({ id }));
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item delete rejected",
      );
      return id;
    },
  });
}

export function useReorderInvoiceLineItems() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { invoiceGroupId: string; orderedIds: string[] },
    { invoiceGroupId: string }
  >({
    failure: "Failed to reorder line items",
    run: async ({ invoiceGroupId, orderedIds }) => {
      const mutation = zero.mutate(
        appMutators.invoiceLineItems.reorder({
          invoiceGroupID: invoiceGroupId,
          orderedIDs: orderedIds,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Line item reorder rejected",
      );
      return { invoiceGroupId };
    },
  });
}

export function useCreateInvoiceDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      invoiceId: string;
      description?: string;
      amount?: number;
      displayOrder?: number;
      sourceInvoiceId?: string;
    },
    InvoiceDeductionItemEntity
  >({
    failure: "Failed to create deduction item",
    run: async (vars) => {
      const id = generateUUID();
      const now = Date.now();
      const mutation = zero.mutate(
        appMutators.invoiceDeductionItems.create({
          id,
          invoiceID: vars.invoiceId,
          description: vars.description ?? "",
          amount: vars.amount ?? 0,
          displayOrder: vars.displayOrder ?? 0,
          sourceInvoiceID: vars.sourceInvoiceId ?? null,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction create rejected",
      );
      return {
        id,
        invoiceID: vars.invoiceId,
        description: vars.description ?? "",
        amount: vars.amount ?? 0,
        displayOrder: vars.displayOrder ?? 0,
        sourceInvoiceID: vars.sourceInvoiceId ?? null,
        createdAt: now,
        updatedAt: now,
      };
    },
  });
}

export function useUpdateInvoiceDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    {
      id: string;
      updates: Partial<{
        description: string;
        amount: number;
        displayOrder: number;
        sourceInvoiceID: string | null;
      }>;
    },
    { id: string }
  >({
    failure: "Failed to update deduction item",
    run: async ({ id, updates }) => {
      const mutation = zero.mutate(
        appMutators.invoiceDeductionItems.update({
          id,
          description: updates.description,
          amount: updates.amount,
          displayOrder: updates.displayOrder,
          sourceInvoiceID: updates.sourceInvoiceID,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction update rejected",
      );
      return { id };
    },
  });
}

export function useDeleteInvoiceDeductionItem() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<{ id: string; invoiceId: string }, string>({
    failure: "Failed to delete deduction item",
    run: async ({ id }) => {
      const mutation = zero.mutate(
        appMutators.invoiceDeductionItems.delete({ id }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Deduction delete rejected",
      );
      return id;
    },
  });
}

export function useCopyQuoteContentToInvoice() {
  const zero = useZero() as ZeroMutateClient;
  return useZeroMutation<
    { sourceQuoteVersionId: string; targetInvoiceId: string },
    { targetInvoiceId: string }
  >({
    failure: "Failed to copy quote content",
    run: async ({ sourceQuoteVersionId, targetInvoiceId }) => {
      const mutation = zero.mutate(
        appMutators.invoiceGroups.copyQuoteContent({
          sourceQuoteVersionID: sourceQuoteVersionId,
          targetInvoiceID: targetInvoiceId,
        }),
      );
      await mutation.client;
      await reportZeroMutationFailure(
        mutation.server,
        "Quote content copy rejected",
      );
      return { targetInvoiceId };
    },
  });
}
